import { BusinessUnitsRestrictionMode, UserRole } from "@prisma/client";
import { match } from "ts-pattern";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { compact, uniq } from "~/lib/lodash";
import { getId } from "~/lib/utils";

export const fetchExternalEmployeeIdsForBusinessUnits = async (ctx: AppContext) => {
  if (!ctx.featureFlags.CAN_ACCESS_BUSINESS_UNITS) {
    return null;
  }

  const user = getRequiredUser(ctx);

  if (user.permissions.role !== UserRole.HR) {
    return null;
  }

  const companyPermissions = await ctx.prisma.companyPermissions.findUnique({
    where: { companyId: user.companyId },
    select: { businessUnitsRestrictionMode: true },
  });

  if (!companyPermissions) {
    return null;
  }

  return match(companyPermissions.businessUnitsRestrictionMode)
    .with(BusinessUnitsRestrictionMode.NONE, () => null)
    .with(BusinessUnitsRestrictionMode.CLASSIC, () => handleClassicBusinessUnitRestriction(ctx))
    .with(BusinessUnitsRestrictionMode.SPECIFIED, () => handleSpecifiedBusinessUnitRestriction(ctx))
    .exhaustive();
};

const handleClassicBusinessUnitRestriction = async (ctx: AppContext) => {
  const user = getRequiredUser(ctx);

  const permissionsWithBusinessUnit = await ctx.prisma.userPermissions.findUnique({
    where: { id: user.permissionsId },
    select: {
      externalEmployee: {
        select: { businessUnit: true, extraBusinessUnits: true },
      },
    },
  });

  if (!permissionsWithBusinessUnit?.externalEmployee?.businessUnit) {
    return null;
  }

  const allowedBusinessUnits = compact(
    uniq([
      permissionsWithBusinessUnit.externalEmployee.businessUnit,
      ...(permissionsWithBusinessUnit.externalEmployee.extraBusinessUnits ?? []),
    ])
  );
  const externalEmployees = await ctx.prisma.externalEmployee.findMany({
    where: {
      companyId: user.companyId,
      businessUnit: { in: allowedBusinessUnits },
    },
    select: { id: true },
  });

  return externalEmployees.map(getId);
};

const handleSpecifiedBusinessUnitRestriction = async (ctx: AppContext) => {
  const user = getRequiredUser(ctx);

  const permissionsWithBusinessUnit = await ctx.prisma.userPermissions.findUnique({
    where: { id: user.permissionsId },
    select: {
      externalEmployee: {
        select: { businessUnit: true, extraBusinessUnits: true },
      },
    },
  });

  const extraBusinessUnits = value(() => {
    if (!permissionsWithBusinessUnit?.externalEmployee?.extraBusinessUnits) {
      return null;
    }

    if (permissionsWithBusinessUnit.externalEmployee.extraBusinessUnits.length === 0) {
      return null;
    }

    return permissionsWithBusinessUnit.externalEmployee.extraBusinessUnits;
  });

  if (!extraBusinessUnits) {
    return null;
  }

  const externalEmployees = await ctx.prisma.externalEmployee.findMany({
    where: {
      companyId: user.companyId,
      businessUnit: { in: extraBusinessUnits },
    },
    select: { id: true },
  });

  return externalEmployees.map(getId);
};
